import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

// Address slot group — mirrors the Footer's address pattern so the visit-us
// card becomes a link to the property's Google Place page when set. The
// schema literal references this constant (extractor follows one level).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time inside PropertyInfoPanel. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const prefix = 'contactform';
  const formBlockPath = `${prefix}.form_block`;
  const heading = useContentToken('contactform.heading');

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });

  return (
    <>
      <div className="cf-section">
        <div className="cf-container">
          <div className="cf-layout">
            {/* Left: section title + lead form */}
            <div className="cf-form-col">
              <EditableToken
                path="contactform.heading"
                as="h2"
                className="cf-heading"
              >
                {heading}
              </EditableToken>
              <div className="cf-form-wrap">
                <EditableToken path={formBlockPath} as="div">
                  {formSlot ? (
                    <SectionContextProvider sectionId={formSlot.sectionId}>
                      <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                    </SectionContextProvider>
                  ) : (
                    <div className="cf-form-empty-card">
                      <p className="cf-form-empty">No form block selected</p>
                    </div>
                  )}
                </EditableToken>
              </div>
            </div>

            {/* Right: contact info cards stacked vertically */}
            <aside className="cf-info-rail">
              <PropertyInfoPanel prefix={prefix} addressDefinition={addressLinkDef} />
            </aside>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="contactform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup
        groupPath="contactform.address"
        definition={addressLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableToken path="contactform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .cf-section {
          background: #fff;
          padding: 2.5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 4rem 2rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
        }

        /* Two-column layout: form on the left, contact info on the right */
        .cf-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: start;
        }
        @media (min-width: 1024px) {
          .cf-layout {
            grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
            gap: 2.5rem;
          }
        }

        .cf-form-col {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          min-width: 0;
        }
        .cf-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 400;
          line-height: 1.2;
          color: var(--vibe-color-text);
          margin: 0;
          text-align: center;
        }
        @media (min-width: 768px) {
          .cf-heading { font-size: 2rem; }
        }

        /* Form card only — shadow/radius on the form wrap, not the info tiles */
        .cf-form-wrap {
          width: 100%;
          /* The lead form draws its input/select/textarea borders from this
             variable, so scoping it here darkens the fields without touching
             borders elsewhere on the site. */
          --vibe-color-border: #6b7280;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow:
            0 24px 56px rgba(0, 0, 0, 0.24),
            0 8px 20px rgba(0, 0, 0, 0.14);
          background: white;
        }
        /* Submit button sits centered at its natural width rather than
           spanning the card, and carries a scaled-down version of the card
           elevation. Width/padding beat the block's inline styles. */
        .cf-form-wrap .mh-lead-form-submit-button {
          display: flex;
          width: fit-content !important;
          min-width: 9rem;
          padding: 0.875rem 2.25rem !important;
          margin-left: auto;
          margin-right: auto;
          font-weight: 400 !important;
          box-shadow:
            0 10px 22px rgba(0, 0, 0, 0.24),
            0 3px 8px rgba(0, 0, 0, 0.14);
          transition:
            background-color 0.2s ease,
            transform 0.15s ease,
            box-shadow 0.2s ease !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow:
            0 14px 28px rgba(0, 0, 0, 0.28),
            0 4px 10px rgba(0, 0, 0, 0.16);
        }
        .cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled) {
          transform: translateY(1px);
          box-shadow:
            0 5px 12px rgba(0, 0, 0, 0.22),
            0 2px 5px rgba(0, 0, 0, 0.12);
        }
        .cf-form-wrap .mh-lead-form-submit-button:disabled {
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.14);
        }
        /* Hide the lead form's built-in "Contact Us" header — replaced by
           contactform.heading above the form card. */
        .cf-form-wrap .mh-lead-form-card > h3:first-of-type {
          display: none !important;
        }
        .cf-form-wrap .mh-lead-form-card > h3:first-of-type + div:empty {
          display: none !important;
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* ── Right rail: plain centered contact info ── */
        .cf-info-rail { width: 100%; }
        /* Phone and tablet layouts show the info in the footer already, and the
           rail only earns its own column at 1024px, so it is dropped below the
           desktop breakpoint. */
        @media (max-width: 1023px) {
          .cf-info-rail { display: none; }
        }
        .cf-info-rail .cf-info-cards {
          flex-direction: column;
          gap: 1.75rem;
        }
        .cf-info-rail .cf-card {
          flex: 0 0 auto;
          width: 100%;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.heading': {
      label: 'Form Heading',
      type: 'text',
      defaultValue: "Let's Talk About Your New Home!",
    },
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'contactform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
